









































































































































import { Component, Prop } from "vue-property-decorator";
import { mixins } from "vue-class-component";
import { getModule } from "vuex-module-decorators";

import FormErrors from "@/lib/components/FormErrors.vue";
import FormField from "@/lib/components/FormField.vue";
import LoadingSpinner from "@/lib/components/LoadingSpinner.vue";
import SubmitButton from "@/lib/components/SubmitButton.vue";

import GeneralFormMixin from "@/lib/mixins/GeneralForm";
import dataStore from "@/store";

import AirwayBillUpload from "./CreateBulkShipmentForm/AirwayBillUpload.vue";

import BulkShipment from "../bulk-shipment.model";
import BulkShipmentBox from "../bulk-shipment-box.model";
import BulkShipmentUpdate from "../bulk-shipment-update.interface";
import DistributionCenter from "../distribution-center.model";
import hasTrackingNumberChanged from "../has-tracking-number-changed.function";
import BulkShipmentStore from "../store";

@Component({
  components: {
    AirwayBillUpload,
    FormErrors,
    FormField,
    LoadingSpinner,
    SubmitButton,
  },
})
export default class BulkShipmentDestinationEditForm extends mixins(
  GeneralFormMixin
) {
  @Prop({ required: true })
  public readonly shipment!: BulkShipment;

  @Prop({ required: true })
  public readonly distributionCenters!: DistributionCenter[];

  public trackingNumber = "";
  public carrier = "";
  public distributionCenter = "";
  public airwayBill = "";

  public boxNumber?: number;
  public length?: number;
  public width?: number;
  public height?: number;
  public weight?: number;
  public isAirwayBillUploadBusy = false;

  public readonly dimensions = [
    { field: "length", label: "Length, cm" },
    { field: "width", label: "Width, cm" },
    { field: "height", label: "Height, cm" },
  ];

  private fBox: BulkShipmentBox | null = null;
  private fIsLoading = true;
  private fLoadFailed = false;
  private fBulkShipmentStore: BulkShipmentStore;

  public constructor() {
    super();
    this.fBulkShipmentStore = getModule(BulkShipmentStore, dataStore);
  }

  get displayId(): string {
    return String(this.shipment.incrementalId || this.shipment.id);
  }

  get box(): BulkShipmentBox | undefined {
    return this.fBox || undefined;
  }

  get isLoading(): boolean {
    return this.fIsLoading;
  }

  get activeDistributionCenters(): DistributionCenter[] {
    return this.distributionCenters.filter((center) => center.isActive);
  }

  get selectedDistributionCenter(): DistributionCenter | undefined {
    return this.activeDistributionCenters.find(
      (center) => center.id === this.distributionCenter
    );
  }

  get isBoxParametersRequired(): boolean {
    const center = this.selectedDistributionCenter;

    if (!center) {
      return false;
    }

    return (
      center.id.toUpperCase() === "PORTLESS" ||
      center.code.toUpperCase() === "PORTLESS" ||
      center.isBoxParametersRequired
    );
  }

  get isTrackingNumberChanged(): boolean {
    return hasTrackingNumberChanged(
      this.trackingNumber,
      this.shipment.trackingNumber
    );
  }

  get isBoxChanged(): boolean {
    if (!this.box) {
      return true;
    }

    return (
      this.boxNumber !== this.box.boxNumber ||
      this.length !== this.box.length ||
      this.width !== this.box.width ||
      this.height !== this.box.height ||
      this.weight !== this.box.weight
    );
  }

  get isDirty(): boolean {
    return (
      Object.keys(this.shipmentUpdate).length > 1 ||
      (this.isBoxParametersRequired && this.isBoxChanged)
    );
  }

  get isDisabled(): boolean {
    return this.fIsDisabled || this.disable;
  }

  get isSubmitDisabled(): boolean {
    return (
      this.isDisabled ||
      this.fLoadFailed ||
      this.isAirwayBillUploadBusy ||
      !this.isDirty
    );
  }

  public created(): void {
    this.trackingNumber = this.shipment.trackingNumber || "";
    this.carrier = this.shipment.carrier;
    this.distributionCenter = this.shipment.distributionCenter;
    this.airwayBill = this.shipment.airwayBill || "";
    void this.loadData();
  }

  public onAirwayBillUploadIsBusyChanged(isBusy: boolean): void {
    this.isAirwayBillUploadBusy = isBusy;
  }

  public onCancelClick(): void {
    this.$emit("cancel");
  }

  protected data(): Record<string, number | undefined> {
    return {
      boxNumber: this.boxNumber,
      length: this.length,
      width: this.width,
      height: this.height,
      weight: this.weight,
    };
  }

  protected async performSubmit(): Promise<void> {
    if (this.isAirwayBillUploadBusy || !this.isDirty) {
      return;
    }

    if (this.isBoxParametersRequired && this.isBoxChanged) {
      await this.saveBox();
    }

    const update = this.shipmentUpdate;

    if (Object.keys(update).length > 1) {
      await this.fBulkShipmentStore.editBulkShipment(update);
    }

    this.$emit("saved");
  }

  private get shipmentUpdate(): BulkShipmentUpdate {
    const update: BulkShipmentUpdate = { id: this.shipment.id };

    if (this.isTrackingNumberChanged) {
      update.trackingNumber = this.trackingNumber;
    }

    if (this.carrier !== this.shipment.carrier) {
      update.carrier = this.carrier;
    }

    if (this.distributionCenter !== this.shipment.distributionCenter) {
      update.distributionCenter = this.distributionCenter;
    }

    if (this.airwayBill !== this.shipment.airwayBill && this.airwayBill) {
      update.airwayBill = this.airwayBill;
    }

    return update;
  }

  private async loadData(): Promise<void> {
    this.fIsLoading = true;
    this.fLoadFailed = false;
    this.submitErrors = [];

    try {
      await this.fBulkShipmentStore.loadBulkShipmentBoxesByIds({
        ids: [this.shipment.id],
        useCache: false,
      });
      this.fBox =
        this.fBulkShipmentStore.getBulkShipmentBoxById(this.shipment.id) ||
        null;

      if (!this.box) {
        return;
      }

      this.boxNumber = this.box.boxNumber;
      this.length = this.box.length;
      this.width = this.box.width;
      this.height = this.box.height;
      this.weight = this.box.weight;
    } catch (error) {
      this.fLoadFailed = true;
      this.submitErrors = this.fErrorConverterService.describeError(error);
    } finally {
      this.fIsLoading = false;
    }
  }

  private async saveBox(): Promise<void> {
    if (
      this.boxNumber === undefined ||
      this.length === undefined ||
      this.width === undefined ||
      this.height === undefined ||
      this.weight === undefined
    ) {
      throw new Error("Complete box information is required.");
    }

    const box = new BulkShipmentBox(
      this.shipment.id,
      this.boxNumber,
      this.length,
      this.width,
      this.height,
      this.weight
    );
    box.isNew = !this.box;

    this.fBox = await this.fBulkShipmentStore.saveBulkShipmentBox(box);
  }
}
